import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { Notification, useNotification } from '@/shared/components/Notification';
import type { SystemSettingsFormState, SystemSettingsField } from '@/types/systemSettings';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  FOOTER_MAX_LENGTH,
  FOOTER_WARNING_THRESHOLD,
  HEX_COLOR_REGEX,
  SYSTEM_SETTINGS_FORM_DEFAULTS,
  URL_PROTOCOL_WHITELIST,
} from '@/types/systemSettings';
import type { SystemSettingsResponse } from '@/services/api';
import { deriveTheme } from '@/utils/applySystemTheme';
import type { ThemeMode } from '@/utils/applySystemTheme';

const previewModes = ['light', 'dark'] as const;
type PreviewMode = (typeof previewModes)[number];

interface ThemePreviewProps {
  mode: PreviewMode;
  logoSrc: string | null;
  primaryColor: string;
  secondaryColor: string;
  footerText: string;
}

const ThemePreview: React.FC<ThemePreviewProps> = ({ mode, logoSrc, primaryColor, secondaryColor, footerText }) => {
  const safePrimary = HEX_COLOR_REGEX.test(primaryColor) ? primaryColor : DEFAULT_PRIMARY_COLOR;
  const safeSecondary = HEX_COLOR_REGEX.test(secondaryColor) ? secondaryColor : DEFAULT_SECONDARY_COLOR;
  const theme = deriveTheme(safePrimary, safeSecondary, mode as ThemeMode);
  const isDark = mode === 'dark';

  const cardClass = `rounded-lg overflow-hidden border transition-colors duration-200 ${
    isDark ? 'shadow-xl' : 'shadow-sm'
  }`;

  return (
    <div
      className={cardClass}
      style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.textBase }}
    >
      <header
        className="flex items-center gap-3 px-5 py-4"
        style={{ backgroundColor: theme.primary, color: theme.primaryContrast }}
      >
        {logoSrc ? (
          <img src={logoSrc} alt="Logotipo" className="h-10 w-10 rounded-md object-contain bg-white/80 p-1" />
        ) : (
          <div className="h-10 w-10 rounded-md bg-white/40 flex items-center justify-center text-sm font-semibold uppercase">
            Logo
          </div>
        )}
        <div>
          <p className="text-xs font-medium opacity-80">Vista previa</p>
          <p className="text-lg font-semibold">Portal administrativo</p>
        </div>
      </header>
      <main
        className="px-6 py-5 space-y-4 border-t"
        style={{ backgroundColor: theme.surfaceMuted, borderColor: theme.border, color: theme.textBase }}
      >
        <h3 className="text-lg font-semibold" style={{ color: theme.textStrong }}>
          Colores corporativos
        </h3>
        <p className="text-sm" style={{ color: theme.textMuted }}>
          El botón principal usa el color secundario seleccionado.
        </p>
        <button
          type="button"
          className="px-4 py-2 rounded-md font-medium shadow transition-colors duration-200"
          style={{ backgroundColor: theme.secondary, color: theme.secondaryContrast }}
        >
          Acción principal
        </button>
      </main>
      <footer
        className="px-6 py-4 text-sm border-t"
        style={{ backgroundColor: theme.footerBackground, borderColor: theme.border, color: theme.footerText }}
      >
        {footerText || '© 2025 SLYRSA. Todos los derechos reservados.'}
      </footer>
    </div>
  );
};

const sanitizeHexInput = (value: string): string => {
  if (!value) return '';
  let next = value.trim().toUpperCase();
  next = next.replace(/[^#0-9A-F]/g, '');
  if (!next.startsWith('#')) {
    next = `#${next.replace(/^#+/, '')}`;
  } else {
    next = `#${next.replace(/^#+/, '')}`;
  }
  if (next === '#') {
    return '#';
  }
  return next.slice(0, 7);
};

const normalizeInitialColor = (value: string | null | undefined, fallback: string): string => {
  const sanitized = sanitizeHexInput(value ?? fallback);
  return HEX_COLOR_REGEX.test(sanitized) ? sanitized.toUpperCase() : fallback;
};

const createFormStateFromSettings = (settings: SystemSettingsResponse | null): SystemSettingsFormState => ({
  logoUrl: settings?.logoUrl ?? '',
  primaryColor: normalizeInitialColor(settings?.primaryColor, DEFAULT_PRIMARY_COLOR),
  secondaryColor: normalizeInitialColor(settings?.secondaryColor, DEFAULT_SECONDARY_COLOR),
  footerText: settings?.footerText ?? '',
});

const validateLogoUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return URL_PROTOCOL_WHITELIST.includes(parsed.protocol);
  } catch {
    return false;
  }
};

const validateForm = (state: SystemSettingsFormState): Partial<Record<SystemSettingsField, string>> => {
  const errors: Partial<Record<SystemSettingsField, string>> = {};

  if (state.logoUrl.trim() && !validateLogoUrl(state.logoUrl.trim())) {
    errors.logoUrl = 'Ingresa una URL válida que inicie con http:// o https://.';
  }

  if (!HEX_COLOR_REGEX.test(state.primaryColor)) {
    errors.primaryColor = 'Ingresa un color hexadecimal válido (#RRGGBB o #RGB).';
  }

  if (!HEX_COLOR_REGEX.test(state.secondaryColor)) {
    errors.secondaryColor = 'Ingresa un color hexadecimal válido (#RRGGBB o #RGB).';
  }

  if (state.footerText.length > FOOTER_MAX_LENGTH) {
    errors.footerText = `El pie de página no puede exceder ${FOOTER_MAX_LENGTH} caracteres.`;
  }

  return errors;
};

const mapValidationMessages = (
  messages: string | string[] | undefined
): {
  fieldErrors: Partial<Record<SystemSettingsField, string>>;
  genericMessages: string[];
} => {
  const fieldErrors: Partial<Record<SystemSettingsField, string>> = {};
  const genericMessages: string[] = [];

  if (!messages) {
    return { fieldErrors, genericMessages };
  }

  const list = Array.isArray(messages) ? messages : [messages];

  for (const message of list) {
    const normalized = message.toLowerCase();
    if (normalized.includes('logo')) {
      fieldErrors.logoUrl = message;
    } else if (normalized.includes('primary')) {
      fieldErrors.primaryColor = message;
    } else if (normalized.includes('secondary')) {
      fieldErrors.secondaryColor = message;
    } else if (normalized.includes('footer')) {
      fieldErrors.footerText = message;
    } else {
      genericMessages.push(message);
    }
  }

  return { fieldErrors, genericMessages };
};

const SystemSettingsView: React.FC = () => {
  const { settings, isLoading, isFetching, isError, error, refetch, updateSettings, isSaving } = useSystemSettings();
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [formState, setFormState] = useState<SystemSettingsFormState>(() => ({
    ...SYSTEM_SETTINGS_FORM_DEFAULTS,
    primaryColor: normalizeInitialColor(SYSTEM_SETTINGS_FORM_DEFAULTS.primaryColor, DEFAULT_PRIMARY_COLOR),
    secondaryColor: normalizeInitialColor(SYSTEM_SETTINGS_FORM_DEFAULTS.secondaryColor, DEFAULT_SECONDARY_COLOR),
  }));
  const [initialState, setInitialState] = useState<SystemSettingsFormState>(() => formState);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<SystemSettingsField, string>>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const isDirty = useMemo(() => JSON.stringify(formState) !== JSON.stringify(initialState), [formState, initialState]);

  useEffect(() => {
    if (settings) {
      const normalized = createFormStateFromSettings(settings);
      setInitialState(normalized);
      if (!hasInitialized || !isDirty) {
        setFormState(normalized);
        setHasInitialized(true);
      }
      setFieldErrors({});
      setGlobalError(null);
      setLogoPreviewError(false);
    }
  }, [settings, isDirty, hasInitialized]);

  const footerCharCount = formState.footerText.length;
  const footerCounterClass = footerCharCount > FOOTER_MAX_LENGTH
    ? 'text-sm text-red-600'
    : footerCharCount >= FOOTER_WARNING_THRESHOLD
      ? 'text-sm text-amber-500'
      : 'text-sm text-gray-500';

  const handleLogoUrlChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormState((prev) => ({ ...prev, logoUrl: value }));
    setFieldErrors((prev) => ({ ...prev, logoUrl: undefined }));
    setLogoPreviewError(false);
  };

  const handleColorPickerChange = (field: 'primaryColor' | 'secondaryColor') => (event: ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeHexInput(event.target.value);
    setFormState((prev) => ({ ...prev, [field]: sanitized }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleHexInputChange = (field: 'primaryColor' | 'secondaryColor') => (event: ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeHexInput(event.target.value);
    setFormState((prev) => ({ ...prev, [field]: sanitized }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleFooterChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setFormState((prev) => ({ ...prev, footerText: value }));
    setFieldErrors((prev) => ({ ...prev, footerText: undefined }));
  };

  const handleReset = () => {
    setFormState(initialState);
    setFieldErrors({});
    setGlobalError(null);
    setLogoPreviewError(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFieldErrors({});
    setGlobalError(null);

    const clientErrors = validateForm(formState);
    if (Object.values(clientErrors).some(Boolean)) {
      setFieldErrors(clientErrors);
      showError('Error de validación', 'Revisa los campos marcados antes de guardar.');
      return;
    }

    const payload = {
      primaryColor: formState.primaryColor.toUpperCase(),
      secondaryColor: formState.secondaryColor.toUpperCase(),
      footerText: formState.footerText,
      ...(formState.logoUrl.trim() ? { logoUrl: formState.logoUrl.trim() } : {}),
    };

    try {
      const updated = await updateSettings(payload);
      const normalized = createFormStateFromSettings(updated);
      setInitialState(normalized);
      setFormState(normalized);
      setFieldErrors({});
      setGlobalError(null);
      setLogoPreviewError(false);
      showSuccess('Configuración guardada', 'Los ajustes del sistema se actualizaron correctamente.');
    } catch (err) {
      const apiError = err as { statusCode?: number; message?: string | string[] };
      if (apiError.statusCode === 400) {
        const { fieldErrors: parsedFieldErrors, genericMessages } = mapValidationMessages(apiError.message);
        setFieldErrors(parsedFieldErrors);
        if (genericMessages.length > 0) {
          setGlobalError(genericMessages.join(' '));
        }
        showError('Error de validación', 'El backend rechazó algunos campos. Verifica la información.');
      } else if (apiError.statusCode === 401 || apiError.statusCode === 403) {
        setGlobalError('Permisos insuficientes para actualizar la configuración.');
        showError('Permisos insuficientes', 'No cuentas con acceso para modificar la configuración.');
      } else {
        const fallback = Array.isArray(apiError.message) ? apiError.message.join(' ') : apiError.message;
        setGlobalError(fallback ?? 'Ocurrió un error inesperado al guardar la configuración.');
        showError('Error al guardar', 'No se pudo actualizar la configuración. Inténtalo de nuevo más tarde.');
      }
    }
  };

  const renderSkeleton = () => (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-live="polite">
      <div className="h-7 bg-gray-200 rounded w-64" />
      <div className="h-4 bg-gray-200 rounded w-96" />
      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
        <div className="space-y-6">
          <div className="h-64 bg-white border border-gray-200 rounded-lg" />
          <div className="h-72 bg-white border border-gray-200 rounded-lg" />
          <div className="h-48 bg-white border border-gray-200 rounded-lg" />
        </div>
        <div className="h-96 bg-white border border-gray-200 rounded-lg" />
      </div>
    </div>
  );

  const renderErrorState = () => {
    const status = error?.statusCode;
    const title = status === 401 || status === 403 ? 'Permisos insuficientes' : 'Error al cargar la configuración';
    const description = status === 401 || status === 403
      ? 'No cuentas con acceso para visualizar la configuración del sistema.'
      : 'No pudimos cargar la configuración actual. Inténtalo nuevamente.';

    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        <div className="border border-red-200 bg-red-50 rounded-lg p-6 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-red-800">{title}</h2>
            <p className="text-red-700 text-sm">{description}</p>
            {error?.message && status !== 401 && status !== 403 && (
              <p className="text-red-600 text-sm mt-2">{Array.isArray(error.message) ? error.message.join(' ') : error.message}</p>
            )}
          </div>
          {(status !== 401 && status !== 403) && (
            <button
              type="button"
              onClick={() => refetch()}
              className={estiloDeBoton('primario')}
            >
              Reintentar
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isLoading && !hasInitialized) {
    return renderSkeleton();
  }

  if (isError && !settings) {
    return renderErrorState();
  }

  const previewLogo = formState.logoUrl.trim() ? formState.logoUrl.trim() : null;

  return (
    <div className="p-6 space-y-6">
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />

      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <TituloDePantalla nivel="interna">Configuración del Sistema</TituloDePantalla>
          {isFetching && !isLoading && (
            <Badge color="azul">Actualizando…</Badge>
          )}
        </div>
        <p className="text-gray-600">
          Gestiona la identidad visual del portal cargando el logotipo, ajustando los colores corporativos y personalizando el pie de página.
        </p>
      </header>

      {globalError && (
        <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-lg p-4 text-sm">
          {globalError}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6 items-start">
        <form className="space-y-6" onSubmit={handleSubmit} noValidate>
          <section className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 space-y-5">
            <header className="space-y-1">
              <h2 className="text-lg font-semibold text-gray-900">Logotipo</h2>
              <p className="text-sm text-gray-600">Indica la URL pública del logotipo corporativo (PNG, JPG o SVG).</p>
            </header>

            <div className="space-y-4">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                <div className="w-full lg:w-auto flex-1">
                  <label htmlFor="logoUrl" className="sr-only">
                    URL del logotipo
                  </label>
                  <input
                    id="logoUrl"
                    name="logoUrl"
                    type="url"
                    inputMode="url"
                    placeholder="https://tu-dominio.com/assets/logo.png"
                    value={formState.logoUrl}
                    onChange={handleLogoUrlChange}
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${fieldErrors.logoUrl ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 text-gray-900'}`}
                    aria-describedby={fieldErrors.logoUrl ? 'logo-url-error' : undefined}
                  />
                  {fieldErrors.logoUrl && (
                    <p id="logo-url-error" className="mt-2 text-sm text-red-600">{fieldErrors.logoUrl}</p>
                  )}
                </div>
              </div>

              {formState.logoUrl.trim() && !logoPreviewError && (
                <div className="max-w-xs">
                  <p className="text-xs text-gray-500 mb-2">Vista previa:</p>
                  <img
                    src={formState.logoUrl.trim()}
                    alt="Vista previa del logotipo"
                    className="rounded-md border border-gray-200 bg-white"
                    onError={() => setLogoPreviewError(true)}
                  />
                </div>
              )}

              {logoPreviewError && (
                <p className="text-xs text-red-600">
                  No se pudo cargar la imagen a partir de la URL proporcionada. Verifica que sea accesible públicamente.
                </p>
              )}
            </div>
          </section>

          <section className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 space-y-5">
            <header className="space-y-1">
              <h2 className="text-lg font-semibold text-gray-900">Colores corporativos</h2>
              <p className="text-sm text-gray-600">Define los tonos principales que se usarán en la interfaz.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label htmlFor="primaryColor" className="text-sm font-medium text-gray-700">
                  Color primario
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="primaryColorPicker"
                    type="color"
                    value={HEX_COLOR_REGEX.test(formState.primaryColor) ? formState.primaryColor : DEFAULT_PRIMARY_COLOR}
                    onChange={handleColorPickerChange('primaryColor')}
                    className="h-10 w-16 border border-gray-300 rounded"
                  />
                  <input
                    id="primaryColor"
                    name="primaryColor"
                    type="text"
                    inputMode="text"
                    value={formState.primaryColor}
                    onChange={handleHexInputChange('primaryColor')}
                    className={`flex-1 border rounded-md px-3 py-2 text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${fieldErrors.primaryColor ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 text-gray-900'}`}
                    aria-describedby={fieldErrors.primaryColor ? 'primary-color-error' : undefined}
                  />
                </div>
                {fieldErrors.primaryColor && (
                  <p id="primary-color-error" className="text-sm text-red-600">{fieldErrors.primaryColor}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="secondaryColor" className="text-sm font-medium text-gray-700">
                  Color secundario
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="secondaryColorPicker"
                    type="color"
                    value={HEX_COLOR_REGEX.test(formState.secondaryColor) ? formState.secondaryColor : DEFAULT_SECONDARY_COLOR}
                    onChange={handleColorPickerChange('secondaryColor')}
                    className="h-10 w-16 border border-gray-300 rounded"
                  />
                  <input
                    id="secondaryColor"
                    name="secondaryColor"
                    type="text"
                    inputMode="text"
                    value={formState.secondaryColor}
                    onChange={handleHexInputChange('secondaryColor')}
                    className={`flex-1 border rounded-md px-3 py-2 text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${fieldErrors.secondaryColor ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 text-gray-900'}`}
                    aria-describedby={fieldErrors.secondaryColor ? 'secondary-color-error' : undefined}
                  />
                </div>
                {fieldErrors.secondaryColor && (
                  <p id="secondary-color-error" className="text-sm text-red-600">{fieldErrors.secondaryColor}</p>
                )}
              </div>
            </div>
          </section>

          <section className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 space-y-4">
            <header className="space-y-1">
              <h2 className="text-lg font-semibold text-gray-900">Pie de página</h2>
              <p className="text-sm text-gray-600">Ingresa el mensaje que aparecerá en la parte inferior del sistema.</p>
            </header>

            <div className="space-y-2">
              <label htmlFor="footerText" className="sr-only">
                Pie de página
              </label>
              <textarea
                id="footerText"
                name="footerText"
                value={formState.footerText}
                onChange={handleFooterChange}
                rows={4}
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${fieldErrors.footerText ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 text-gray-900'}`}
                aria-describedby="footer-helper"
                maxLength={FOOTER_MAX_LENGTH + 100}
              />
              <div className="flex items-center justify-between">
                <span id="footer-helper" className={footerCounterClass}>
                  {footerCharCount}/{FOOTER_MAX_LENGTH} caracteres
                </span>
                {fieldErrors.footerText && (
                  <p className="text-sm text-red-600">{fieldErrors.footerText}</p>
                )}
              </div>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              className={estiloDeBoton('secundario')}
              disabled={!isDirty || isSaving}
            >
              Restablecer valores
            </button>
            <button
              type="submit"
              className={estiloDeBoton('primario')}
              disabled={isSaving}
            >
              {isSaving ? 'Guardando…' : 'Guardar configuración'}
            </button>
          </div>
        </form>

        <aside className="bg-white shadow-sm border border-gray-200 rounded-lg p-5 space-y-6">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Vista previa</h2>
            <p className="text-sm text-gray-600">Compara la apariencia en modo claro y oscuro antes de publicar.</p>
          </div>

          <div className="space-y-6">
            {previewModes.map((mode) => (
              <div key={mode} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">
                    {mode === 'light' ? 'Modo claro' : 'Modo oscuro'}
                  </h3>
                  <span className="text-xs uppercase tracking-wide text-gray-400">{mode}</span>
                </div>
                <ThemePreview
                  mode={mode}
                  logoSrc={previewLogo}
                  primaryColor={formState.primaryColor}
                  secondaryColor={formState.secondaryColor}
                  footerText={formState.footerText}
                />
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SystemSettingsView;
